import React, { useEffect, useState } from 'react';
import { Package, Plus, Edit2, Trash2, X, AlertCircle, Search, MapPin, Phone, User } from 'lucide-react';
import { getGudang, createGudang, updateGudang, deleteGudang } from '../../api';
import { useAuth } from '../../AuthContext';

const emptyForm = { kode: '', nama: '', alamat: '', pic: '', telp: '', keterangan: '' };

export default function Gudang() {
  const { hasMenuAction } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    getGudang().then(res => setData(res.data)).catch(console.error).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // Auto-refresh when tab becomes visible
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = data.filter(d =>
    !search || d.nama?.toLowerCase().includes(search.toLowerCase()) || d.kode?.toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => { setEditing(null); setForm(emptyForm); setError(''); setShowModal(true); };
  const openEdit = (item) => { setEditing(item); setForm(item); setError(''); setShowModal(true); };

  const handleSave = async () => {
    setError('');
    if (!form.kode || !form.nama) { setError('Kode dan nama harus diisi'); return; }
    setSaving(true);
    try {
      if (editing) await updateGudang(editing.id, form);
      else await createGudang(form);
      setShowModal(false); load();
    } catch (err) { setError(err.response?.data?.error || 'Gagal menyimpan'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Yakin hapus gudang "${item.nama}"?`)) return;
    try { await deleteGudang(item.id); load(); }
    catch (err) { alert(err.response?.data?.error || 'Gagal hapus'); }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-row">
          <div>
            <h2>Master Gudang</h2>
            <p className="page-desc">{data.length} gudang terdaftar</p>
          </div>
          {hasMenuAction('inventori', 'create') && (
            <button className="btn btn-primary" onClick={openCreate}>
              <Plus size={16} /><span>Tambah Gudang</span>
            </button>
          )}
        </div>
      </div>
      <div className="search-box">
        <Search size={18} />
        <input type="text" placeholder="Cari gudang..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {loading ? <div className="page-loading">Memuat data...</div> : (
        <div className="regions-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}>
          {filtered.map(d => (
            <div key={d.id} className="region-card">
              <div className="region-card-header" style={{ background: 'linear-gradient(135deg, #11cdef, #0e8c8c)' }}>
                <Package size={22} />
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: '0.95rem' }}>{d.nama}</h3>
                  <small style={{ opacity: 0.8 }}>{d.kode}</small>
                </div>
              </div>
              <div className="region-card-body">
                <div className="region-detail"><MapPin size={14} className="text-muted" /><strong>Alamat:</strong> {d.alamat || '-'}</div>
                <div className="region-detail"><User size={14} className="text-muted" /><strong>PIC:</strong> {d.pic || '-'}</div>
                <div className="region-detail"><Phone size={14} className="text-muted" /><strong>Telp:</strong> {d.telp || '-'}</div>
                <div className="action-btns" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
                  {hasMenuAction('inventori', 'edit') && <button className="btn-icon" title="Edit" onClick={() => openEdit(d)}><Edit2 size={14} /></button>}
                  {hasMenuAction('inventori', 'delete') && <button className="btn-icon btn-danger" title="Hapus" onClick={() => handleDelete(d)}><Trash2 size={14} /></button>}
                </div>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className="page-loading" style={{ gridColumn: '1/-1' }}>Tidak ada data gudang</div>}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editing ? 'Edit Gudang' : 'Tambah Gudang'}</h3>
              <button className="btn-icon" onClick={() => setShowModal(false)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              {error && <div className="form-error"><AlertCircle size={14} /><span>{error}</span></div>}
              <div className="form-grid">
                <div className="form-group"><label>Kode *</label><input type="text" value={form.kode} onChange={e => setForm({...form, kode: e.target.value})} placeholder="GDG-001" /></div>
                <div className="form-group"><label>Nama *</label><input type="text" value={form.nama} onChange={e => setForm({...form, nama: e.target.value})} placeholder="Nama gudang" /></div>
                <div className="form-group"><label>Alamat</label><textarea rows={2} value={form.alamat} onChange={e => setForm({...form, alamat: e.target.value})} /></div>
                <div className="form-group"><label>PIC</label><input type="text" value={form.pic} onChange={e => setForm({...form, pic: e.target.value})} placeholder="Penanggung jawab" /></div>
                <div className="form-group"><label>Telp</label><input type="text" value={form.telp} onChange={e => setForm({...form, telp: e.target.value})} placeholder="No telepon" /></div>
                <div className="form-group"><label>Keterangan</label><input type="text" value={form.keterangan} onChange={e => setForm({...form, keterangan: e.target.value})} /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Batal</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Menyimpan...' : 'Simpan'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
